import React, { useRef, useState, useEffect } from 'react';
import { FaFilePdf, FaPills } from 'react-icons/fa6';
import './ABCDEFProcedures.scss';
import { useResusContext } from '../Resus/ResusContext';
import { getHospitalData } from './PerHospitalResusConfig';
import { cprEventEmitter, EVENTS } from './cprEvents';
import { useTranslation } from 'react-i18next';

interface ReversibleFactor {
  name: string;
  protocolId?: string;
  medsSection?: string;
  medicationHint?: string;
}

const StarBullet = () => (
  <svg width="14" height="14" viewBox="0 0 105 105" fill="none" xmlns="http://www.w3.org/2000/svg" className="star-bullet">
    <path d="M52.498 70.2187C53.1936 70.2187 53.8893 70.385 54.5236 70.7131L71.013 79.3319L67.8761 61.145C67.6268 59.7188 68.1036 58.2663 69.1449 57.2556L82.4274 44.4106L64.0393 41.7331C62.6174 41.5275 61.388 40.6394 60.7493 39.3531L52.498 22.7544L44.2468 39.3531C43.608 40.6394 42.3786 41.5275 40.9568 41.7331L22.5686 44.4106L35.8511 57.2556C36.8924 58.2663 37.3693 59.7188 37.1199 61.145L33.983 79.3319L50.4724 70.7131C51.1068 70.385 51.8024 70.2187 52.498 70.2187ZM76.8318 91.6781C76.1361 91.6781 75.4405 91.5162 74.8061 91.1837L52.498 79.5287L30.1899 91.1837C28.7111 91.9537 26.9261 91.8181 25.5918 90.8381C24.2443 89.8581 23.5749 88.2 23.8549 86.5594L28.1074 61.9369L10.0824 44.5025C8.87927 43.3431 8.45052 41.6019 8.96239 40.0138C9.46989 38.4256 10.8436 37.2706 12.493 37.03L37.4349 33.4075L48.578 10.9813C50.0568 8.00625 54.9393 8.00625 56.418 10.9813L67.5611 33.4075L92.503 37.03C94.1524 37.2706 95.5261 38.4256 96.0336 40.0138C96.5455 41.6019 96.1168 43.3431 94.9136 44.5025L76.8886 61.9369L81.1411 86.5594C81.4211 88.2 80.7518 89.8581 79.4043 90.8381C78.6474 91.3981 77.7418 91.6781 76.8318 91.6781Z" fill="#0C3058" />
  </svg>
);
const MedicationHintBalloon = ({ hint, sectionName, onClose }: { hint: string, sectionName: string, onClose: () => void }) => {
  const balloonRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top?: string, right?: string, bottom?: string, left?: string }>({});
  const { t } = useTranslation(['resus']);

  useEffect(() => {
    // Wait for the component to render first
    setTimeout(() => {
      if (!balloonRef.current) return;

      const calculatePosition = () => {
        const balloon = balloonRef.current;
        if (!balloon) return;

        // Get container dimensions
        const cprMain = document.getElementById('cpr-main') || document.body;
        const containerRect = cprMain.getBoundingClientRect();

        // Get the balloon's parent (the button wrapper)
        const parentElement = balloon.parentElement;
        if (!parentElement) return;

        const parentRect = parentElement.getBoundingClientRect();

        // Initially position the balloon to make it visible so we can measure it
        balloon.style.visibility = 'hidden';
        balloon.style.top = 'auto';
        balloon.style.bottom = '30px';
        balloon.style.right = '-10px';
        balloon.style.left = 'auto';

        // Now we can get accurate measurements
        const balloonRect = balloon.getBoundingClientRect();

        // In RTL mode, check if balloon overflows left side of container
        const leftOverflow = parentRect.right - balloonRect.width < containerRect.left;

        // Check if balloon overflows top
        const topOverflow = parentRect.top - balloonRect.height < containerRect.top;

        const newPosition: { top?: string, right?: string, bottom?: string, left?: string } = {};

        // Vertical positioning
        if (topOverflow) {
          // Place below if no space above
          newPosition.top = '30px';
          newPosition.bottom = 'auto';
        } else {
          // Default is above
          newPosition.top = 'auto';
          newPosition.bottom = '30px';
        }

        // Horizontal positioning (in RTL)
        if (leftOverflow) {
          // If it overflows left in RTL, position to the right of button
          newPosition.right = 'auto';
          newPosition.left = '-10px';
        } else {
          // Default RTL position is to the left of button
          newPosition.right = '-10px';
          newPosition.left = 'auto';
        }

        // Apply the position
        setPosition(newPosition);

        // Make balloon visible again
        balloon.style.visibility = 'visible';
      };

      calculatePosition();

      // Recalculate on window resize
      window.addEventListener('resize', calculatePosition);
      return () => window.removeEventListener('resize', calculatePosition);
    }, 0);
  }, []);

  const handleGoToMeds = () => {
    cprEventEmitter.emit(EVENTS.ADD_MEDS_SECTION, sectionName);
    onClose();
  };

  return (
    <div
      className="medication-hint-balloon"
      ref={balloonRef}
      style={position}
    >
      <div className="hint-content">
        <p>{hint}</p>
        <button
          className="med-link"
          onClick={handleGoToMeds}
        >
          {t('abcdef.goToMedications')}
        </button>
      </div>
      <button className="close-hint" onClick={onClose}>×</button>
    </div>
  );
};
const AdditionalLinks = ({
  protocolId,
  medsSection,
  medicationHint,
  factorIndex,
  activeHintId,
  setActiveHintId
}: {
  protocolId?: string,
  medsSection?: string,
  medicationHint?: string,
  factorIndex: number,
  activeHintId: number | null,
  setActiveHintId: (id: number | null) => void
}) => {
  const { hospital, protocols } = useResusContext();
  const hospitalData = getHospitalData(hospital);
  const emergencyProtocols = hospitalData.protocols;

  const getProtocolFile = (id: string) => {
    const protocols = emergencyProtocols.emergencyProtocols.flatMap(section => section.protocols);
    const protocol = protocols.find(p => p.id === id);
    if (!protocol) return null;
    
    // Handle new protocol structure with attachments
    if (protocol.attachments && protocol.attachments.length > 0) {
      // First, try to find the algorithm attachment
      const algorithmAttachment = protocol.attachments.find(att => att.name === "אלגוריתם");
      if (algorithmAttachment) return algorithmAttachment.file;
      
      // If not found, try to find the protocol attachment
      const protocolAttachment = protocol.attachments.find(att => att.name === "פרוטוקול");
      if (protocolAttachment) return protocolAttachment.file;
      
      // If neither is found, take the first attachment in the list
      return protocol.attachments[0].file;
    }
    
    // Fallback to old structure for backward compatibility
    return protocol.algorithmFile || protocol.protocolFile;
  };

  if (!protocolId && !medsSection) return null;
  const showLinkToMeds = medsSection && protocols.includes("cardiac-arrest");

  const toggleHint = (e: React.MouseEvent) => {
    e.stopPropagation();
    // If this hint is already active, close it
    if (activeHintId === factorIndex) {
      setActiveHintId(null);
    } else {
      // Otherwise, activate this hint (and automatically close any other)
      setActiveHintId(factorIndex);
    }
  };

  const closeHint = () => {
    setActiveHintId(null);
  };

  return (
    <div className="additional-links-container">
      {protocolId && (
        <a
          href={`${hospitalData.protocols_base_folder}${getProtocolFile(protocolId || '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          <FaFilePdf style={{ color: '#F77389', marginRight: '3px', marginLeft: '13px' }} />
        </a>
      )}
      {showLinkToMeds && medicationHint && (
        <div className="hint-wrapper">
          <button
            onClick={toggleHint}
            className="btn btn-link med-btn"
          >
            <FaPills />
          </button>
          {activeHintId === factorIndex && (
            <MedicationHintBalloon
              hint={medicationHint}
              sectionName="cardiac-arrest"
              onClose={closeHint}
            />
          )}
        </div>
      )}
      {showLinkToMeds && !medicationHint && (
        <button
          onClick={() => cprEventEmitter.emit(EVENTS.ADD_MEDS_SECTION, "cardiac-arrest")}
          className="btn btn-link"
        >
          <FaPills />
        </button>
      )}
    </div>
  );
};

const ABCDEFProcedures: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeHintId, setActiveHintId] = useState<number | null>(null);
  const { t } = useTranslation(['resus']);

  const reversibleFactors: ReversibleFactor[] = [
    { name: t('abcdef.reversibleFactors.hypovolemia'), medsSection: "cardiac-arrest", medicationHint: t('abcdef.medicationHints.nacl') },
    { name: t('abcdef.reversibleFactors.hypoxia') },
    { name: t('abcdef.reversibleFactors.acidosis'), medsSection: "cardiac-arrest", medicationHint: t('abcdef.medicationHints.bicarbonate') },
    { name: t('abcdef.reversibleFactors.hypoglycemia'), protocolId: 'hypoglycemia', medsSection: "cardiac-arrest", medicationHint: t('abcdef.medicationHints.dextrose') },
    { name: t('abcdef.reversibleFactors.hypokalemia'), protocolId: 'hypokalemia' },
    { name: t('abcdef.reversibleFactors.hyperkalemia'), protocolId: 'hyperkalemia', medsSection: "cardiac-arrest", medicationHint: t('abcdef.medicationHints.calciumGluconate') },
    { name: t('abcdef.reversibleFactors.hypothermia') },
    { name: t('abcdef.reversibleFactors.tensionPneumothorax') },
    { name: t('abcdef.reversibleFactors.cardiacTamponade') },
    { name: t('abcdef.reversibleFactors.toxins') },
    { name: t('abcdef.reversibleFactors.pulmonaryEmbolism') },
    { name: t('abcdef.reversibleFactors.coronaryThrombosis') }
  ];

  const procedures = [
    {
      letter: 'A',
      title: 'AIRWAY',
      content: [
        t('abcdef.procedures.a.openAirway'),
        t('abcdef.procedures.a.bloodSecretions'),
        t('abcdef.procedures.a.suction'),
        t('abcdef.procedures.a.teethFractures')
      ]
    },
    {
      letter: 'B',
      title: 'Breathing',
      content: [
        t('abcdef.procedures.b.respiratoryEffort'),
        t('abcdef.procedures.b.chestExpansion'),
        t('abcdef.procedures.b.auscultation'),
        t('abcdef.procedures.b.saturationMeasurement')
      ]
    },
    {
      letter: 'C',
      title: 'Circulation',
      content: [
        t('abcdef.procedures.c.capillaryRefill'),
        t('abcdef.procedures.c.centralPeripheralPulse'),
        t('abcdef.procedures.c.distendedAbdomen'),
        t('abcdef.procedures.c.bleedingControl')
      ]
    },
    {
      letter: 'D',
      title: 'Disability',
      content: [
        t('abcdef.procedures.d.consciousness'),
        t('abcdef.procedures.d.pupils'),
        t('abcdef.procedures.d.movement'),
        t('abcdef.procedures.d.dex')
      ]
    },
    {
      letter: 'E',
      title: 'Environment',
      content: [
        t('abcdef.procedures.e.exposureRolling'),
        t('abcdef.procedures.e.bodyTemperature')
      ]
    },
    {
      letter: 'F',
      title: 'Family',
      content: [
        t('abcdef.procedures.f.familyAnamnesis')
      ]
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close hint after 4 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (activeHintId !== null) {
      timer = setTimeout(() => {
        setActiveHintId(null);
      }, 4000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [activeHintId]);

  const isHighlighted = (letter: string, index: number) => {
    if (isMobile) {
      return index % 2 === 0;
    } else {
      return ['A', 'D', 'E'].includes(letter);
    }
  };

  return (
    <div className="abcdef-container">
      <div className="abcdef-grid">
        {procedures.map((proc, index) => (
          <div
            key={proc.letter}
            className={`card ${isHighlighted(proc.letter, index) ? 'highlighted' : 'white'}`}
          >
            <div className="card-header">
              <h3>
                <span className="letter">{proc.letter}</span>{proc.title.slice(1)}
              </h3>
            </div>
            <div className="card-content">
              <ul>
                {proc.content.map((item, idx) => (
                  <li key={idx} className={(item === t('abcdef.procedures.d.dex') || item === t('abcdef.procedures.e.bodyTemperature')) ? 'bold' : ''}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="reversible-factors">
        <h3>{t('abcdef.reversibleFactorsTitle')}</h3>
        <div className="factors-list">
          {reversibleFactors.map((factor, index) => (
            <div key={index} className="factor-item">
              <StarBullet />
              <span>
                {factor.name}
                <AdditionalLinks
                  protocolId={factor.protocolId}
                  medsSection={factor.medsSection}
                  medicationHint={factor.medicationHint}
                  factorIndex={index}
                  activeHintId={activeHintId}
                  setActiveHintId={setActiveHintId}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ABCDEFProcedures;